import React, { useEffect, useMemo, useState } from "react";
import Context from "../../Context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { Header, Loading } from "../../components/modules";
import { Badge, Spinner } from "reactstrap";
import { FiCreditCard } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";

const formatDate = (ts) =>
  ts
    ? new Date(Number(ts)).toLocaleDateString("th-TH", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      }) + " น."
    : "-";

const StatCard = ({ label, value, color, suffix = "ใบ" }) => (
  <div
    className={`flex flex-col h-fit py-1 px-3 ${color} text-white rounded-lg duration-300 shadow-md hover:shadow-xl`}
  >
    <p className="text-sm font-semibold opacity-80 py-1">{label}</p>
    <div className="text-xl font-bold text-right">
      {value === "loading" ? (
        <Loading className="w-4 h-4 text-bold" />
      ) : typeof value === "number" ? (
        value.toLocaleString()
      ) : (
        value || 0
      )}
      <p className="text-sm text-right font-semibold opacity-80 py-1">
        {suffix}
      </p>
    </div>
  </div>
);

const StatusPill = ({ children, tone }) => {
  const classes = {
    success: "bg-[#52B788]",
    danger: "bg-danger",
    blue: "bg-blue",
    gray: "bg-gray-500",
  };

  return (
    <div
      className={`${classes[tone] || classes.gray} w-28 py-2 px-2 rounded-lg text-center text-sm text-white font-semibold mx-auto`}
    >
      {children}
    </div>
  );
};

const LotteriesDetail = () => {
  const { callApi, configs } = Context();
  const [lotteries, setLotteries] = useState([]);
  const [summary, setSummary] = useState({
    isSold: 0,
    isOnBlock: 0,
    uploader: [],
  });
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const fileNameId = searchParams.get("fileNameId");

  const getData = async () => {
    setLoading(true);
    const result = await callApi(
      "lotteries-getAll",
      {
        queries: [
          ["fileNameId", "==", fileNameId],
          ["roundDate", "==", configs.roundDate],
        ],
        limit: 200,
      },
      { notShowLoading: true },
    );

    const nextLotteries = result || [];
    const nextSummary = nextLotteries.reduce(
      (acc, lotto) => {
        if (lotto.isSold) acc.isSold += 1;
        if (lotto.isOnBlock) acc.isOnBlock += 1;
        if (lotto.uploader && !acc.uploader.includes(lotto.uploader)) {
          acc.uploader.push(lotto.uploader);
        }
        return acc;
      },
      { isSold: 0, isOnBlock: 0, uploader: [] },
    );

    setSummary(nextSummary);
    setLotteries(nextLotteries);
    setLoading(false);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileNameId, configs.roundDate]);

  const sortedLotteries = useMemo(
    () =>
      [...lotteries].sort((a, b) => {
        const aIndex = Number(a?.id?.slice(-2)) || 0;
        const bIndex = Number(b?.id?.slice(-2)) || 0;
        return aIndex - bIndex;
      }),
    [lotteries],
  );

  const soldPercent = lotteries.length
    ? ((summary.isSold || 0) / lotteries.length) * 100
    : 0;

  const stats = [
    {
      label: "สลากทั้งหมด",
      value: loading ? "loading" : lotteries.length,
      color: "bg-blue",
    },
    {
      label: "สลากหน้าบ้าน",
      value: loading ? "loading" : summary.isOnBlock || 0,
      color: "bg-teal-500",
    },
    {
      label: "สลากหลังบ้าน",
      value: loading ? "loading" : lotteries.length - (summary.isOnBlock || 0),
      color: "bg-gray-500",
    },
    {
      label: "ขายไปแล้ว",
      value: loading ? "loading" : summary.isSold || 0,
      color: "bg-[#52B788]",
    },
    {
      label: "สัดส่วนขายแล้ว",
      value: loading ? "loading" : soldPercent.toFixed(2),
      color: "bg-danger",
      suffix: "%",
    },
  ];

  if (!loading && lotteries.length === 0) {
    return (
      <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto">
        <Header
          header="Lotteries"
          subHeader={`รายละเอียดเล่ม ${fileNameId || "-"}`}
          icon={<FiCreditCard className="text-blue" />}
        />
        <div className="mt-12 bg-white w-full rounded-lg shadow-md border border-[#E5E7E9] overflow-hidden min-h-[400px] flex justify-center items-center text-xl font-semibold text-gray-400">
          ไม่พบไฟล์ {fileNameId} ในระบบ
        </div>
      </div>
    );
  }

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto">
      <Header
        header="Lotteries"
        subHeader={`รายละเอียดเล่ม ${fileNameId || "-"}`}
        icon={<FiCreditCard className="text-blue" />}
      />

      <div className="mt-12 mb-6 grid grid-cols-5 gap-2">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} />
        ))}
      </div>

      <div className="relative bg-white w-full rounded-lg shadow-md border border-[#E5E7E9] overflow-hidden">
        <div className="p-3 border-b border-[#E5E7E9]">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-lg">ข้อมูลเล่ม {fileNameId}</p>
              <p className="text-sm text-gray-500 mt-1">
                แสดงรายละเอียดสลากในเล่ม พร้อมสถานะขายและสถานะหน้าบ้าน
              </p>
            </div>
            <button
              disabled={loading}
              className="flex font-semibold items-center min-w-8 gap-1 py-2 px-3 bg-blue text-white text-sm rounded-lg shadow-md hover:opacity-60 transition-all duration-200 disabled:opacity-45 disabled:cursor-not-allowed"
              onClick={getData}
            >
              รีเฟรช
            </button>
          </div>

          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <p className="text-sm font-semibold">ผู้นำเข้า:</p>
            {(summary.uploader || []).map((uploader) => (
              <Badge key={uploader} color="primary" pill>
                {uploader}
              </Badge>
            ))}
          </div>
        </div>

        <div className="relative min-h-[450px] h-full">
          {loading && (
            <div className="absolute w-full h-full flex justify-center items-center bg-white opacity-65 z-20">
              <Loading className="font-bold w-4 h-4" />
            </div>
          )}

          <Table>
            <TableHeader className="bg-[#f2f3f5] border-y border-gray-300">
              <TableRow>
                <TableHead className="text-center">ลำดับที่</TableHead>
                <TableHead className="text-center">เลขสลาก</TableHead>
                <TableHead className="text-center">วันที่นำเข้า</TableHead>
                <TableHead className="text-center">สถานะการขาย</TableHead>
                <TableHead className="text-center">สถานะระบบ</TableHead>
                <TableHead className="text-center">ผู้นำเข้า</TableHead>
                <TableHead className="text-center">รูป</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedLotteries.length > 0 ? (
                sortedLotteries.map((lot, index) => (
                  <TableRow key={lot.id} className="h-20">
                    <TableCell className="font-medium text-center">
                      {index + 1}
                    </TableCell>
                    <TableCell className="font-semibold text-center font-mono tracking-widest">
                      <div>{lot.number || "-"}</div>
                      <div className="text-xs text-gray-400 mt-1">{lot.id}</div>
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {formatDate(lot.createdRawAt || lot.createdAt)}
                    </TableCell>
                    <TableCell className="text-center">
                      {lot.isSold ? (
                        <StatusPill tone="success">
                          ขายแล้ว #{lot.orderId.slice(-6).toUpperCase()}
                        </StatusPill>
                      ) : (
                        <StatusPill tone="danger">ยังไม่ขาย</StatusPill>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {lot.isOnBlock ? (
                        <StatusPill tone="blue">อยู่หน้าบ้าน</StatusPill>
                      ) : (
                        <StatusPill tone="gray">อยู่หลังบ้าน</StatusPill>
                      )}
                    </TableCell>
                    <TableCell className="text-center font-medium">
                      {lot.uploader || "-"}
                    </TableCell>
                    <TableCell className="w-36">
                      <div className="mx-auto w-28 rounded-md overflow-hidden shadow-sm border border-gray-200 bg-white">
                        <img
                          src={lot.image}
                          alt={lot.id}
                          className="w-full transition-transform duration-200 hover:scale-150 origin-center"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan="7">
                    <div className="w-full flex justify-center items-center h-96 font-semibold text-gray-400">
                      {loading ? <Spinner className="w-4 h-4" /> : "ไม่พบสลาก"}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default LotteriesDetail;
